// src/userAuth/UserAuth.jsx

import React, {
  useEffect,
  useState,
} from "react";

import {
  Button,
  Card,
  Form,
  Input,
  Typography,
} from "antd";

import {
  BookOutlined,
  FileDoneOutlined,
  GlobalOutlined,
  LockOutlined,
  LoginOutlined,
  UserOutlined,
} from "@ant-design/icons";

import CenteredMessage from "../components/Comman/Notification/CenteredMessage";

import {
  PORTAL_API,
  PORTAL_URL,
  getUnitApiBase,
  SUB_URL,
} from "../services/BaseUrl";

const {
  Title,
  Text,
} = Typography;

const LOGIN_TAB = {
  SMART: "smart",
  ASSESSMENT: "assessment",
};

const TRAINING_MATERIAL_PATH =
  "/training-material";

const parseUsername = (
  username
) => {
  const value =
    String(
      username || ""
    ).trim();

  const match =
    value.match(
      /^(.*?)(@[^@\s]+)$/
    );

  if (!match) {
    return null;
  }

  const userPart =
    match[1]?.trim();

  const loginTag =
    match[2]?.trim();

  const unitTag =
    loginTag
      .replace("@", "")
      .trim();

  if (
    !userPart ||
    !unitTag
  ) {
    return null;
  }

  return {
    userPart,
    loginTag,
    unitTag,
  };
};

const getInitialTab =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return LOGIN_TAB.SMART;
    }

    const params =
      new URLSearchParams(
        window.location.search
      );

    const requested =
      String(
        params.get("tab") || ""
      )
        .trim()
        .toLowerCase();

    return (
      requested ===
      LOGIN_TAB.ASSESSMENT
    )
      ? LOGIN_TAB.ASSESSMENT
      : LOGIN_TAB.SMART;
  };

function UserAuth({
  onLoginSuccess,
}) {
  const [
    activeTab,
    setActiveTab,
  ] =
    useState(
      getInitialTab
    );

  const [
    username,
    setUsername,
  ] =
    useState("");

  const [
    password,
    setPassword,
  ] =
    useState("");

  const [
    loading,
    setLoading,
  ] =
    useState(false);

  const [
    units,
    setUnits,
  ] =
    useState([]);

  const [
    unitsLoading,
    setUnitsLoading,
  ] =
    useState(true);

  const [
    form,
  ] =
    Form.useForm();

  useEffect(() => {
    const loadUnits =
      async () => {
        try {
          setUnitsLoading(
            true
          );

          const response =
            await fetch(
              `${PORTAL_API.BASE}${PORTAL_API.API_ROOT}${PORTAL_URL.get_units_for_webapp}`,
              {
                method:
                  "POST",
              }
            );

          const data =
            await response.json();

          if (
            data?.status &&
            Array.isArray(
              data?.data
            )
          ) {
            setUnits(
              data.data
            );
          } else {
            CenteredMessage.error(
              data?.message ||
                "Failed to load unit list"
            );
          }
        } catch (
          error
        ) {
          console.error(
            "Unit loading error:",
            error
          );

          CenteredMessage.error(
            "Unit list network error"
          );
        } finally {
          setUnitsLoading(
            false
          );
        }
      };

    loadUnits();

    localStorage.removeItem(
      "isLoggedIn"
    );

    localStorage.removeItem(
      "userData"
    );

    localStorage.removeItem(
      "unit_code"
    );

    localStorage.removeItem(
      "unit_name"
    );

    localStorage.removeItem(
      "nav_message"
    );

    localStorage.removeItem(
      "examUserData"
    );

    localStorage.removeItem(
      "exam_user_id"
    );

    localStorage.removeItem(
      "exam_dept_id"
    );

    localStorage.removeItem(
      "exam_unit_code"
    );

    localStorage.removeItem(
      "exam_unit_name"
    );
  }, []);

  const getLoginContext =
    () => {
      const parsed =
        parseUsername(
          username
        );

      if (!parsed) {
        CenteredMessage.error(
          "Username must be like tracker@unit4"
        );

        return null;
      }

      if (
        unitsLoading
      ) {
        CenteredMessage.warning(
          "Units are still loading, try again..."
        );

        return null;
      }

      const unitRow =
        units.find(
          (unit) =>
            String(
              unit?.login_name ||
                ""
            )
              .trim()
              .toLowerCase() ===
            parsed.loginTag.toLowerCase()
        );

      if (!unitRow) {
        CenteredMessage.error(
          `Unit not found for ${parsed.loginTag}`
        );

        return null;
      }

      return {
        parsed,
        unitCode:
          unitRow.unit_code,
        unitName:
          unitRow.unit_name,
      };
    };

  const callLoginApi =
    async (
      context
    ) => {
      const formData =
        new FormData();

      formData.append(
        "username",
        context
          .parsed
          .userPart
      );

      formData.append(
        "password",
        password
      );

      const loginUrl =
        `${getUnitApiBase(
          context.unitCode
        )}${SUB_URL.login_webapp}`;

      const response =
        await fetch(
          loginUrl,
          {
            method:
              "POST",

            body:
              formData,
          }
        );

      return response.json();
    };

  const handleSmartLogin =
    async () => {
      const context =
        getLoginContext();

      if (!context) {
        return;
      }

      setLoading(true);

      try {
        const data =
          await callLoginApi(
            context
          );

        if (
          !data?.status
        ) {
          CenteredMessage.error(
            data?.message ||
              "Invalid Username or Password"
          );

          return;
        }

        localStorage.setItem(
          "userData",
          JSON.stringify(
            data
          )
        );

        localStorage.setItem(
          "isLoggedIn",
          "true"
        );

        localStorage.setItem(
          "unit_code",
          String(
            context.unitCode
          )
        );

        localStorage.setItem(
          "unit_name",
          String(
            context.unitName ||
              ""
          )
        );

        localStorage.removeItem(
          "nav_message"
        );

        onLoginSuccess?.(
          data
        );

        CenteredMessage.success(
          data?.message ||
            "Login Successful!"
        );
      } catch (
        error
      ) {
        console.error(
          "SM-ART login error:",
          error
        );

        CenteredMessage.error(
          "Network error. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

  const handleAssessmentLogin =
    async () => {
      const context =
        getLoginContext();

      if (!context) {
        return;
      }

      setLoading(true);

      try {
        const data =
          await callLoginApi(
            context
          );

        if (
          !data?.status
        ) {
          CenteredMessage.error(
            data?.message ||
              "Invalid Username or Password"
          );

          return;
        }

        const loginData =
          data?.data || {};

        const userId =
          loginData?.su_id ||
          loginData?.user_id ||
          loginData?.id;

        const deptId =
          loginData?.dept ||
          loginData?.dept_id ||
          loginData?.department_id;

        if (!userId) {
          CenteredMessage.error(
            "User ID not found in login response"
          );

          return;
        }

        if (!deptId) {
          CenteredMessage.error(
            "Department ID not found in login response"
          );

          return;
        }

        localStorage.setItem(
          "examUserData",
          JSON.stringify(
            data
          )
        );

        localStorage.setItem(
          "exam_user_id",
          String(
            userId
          )
        );

        localStorage.setItem(
          "exam_dept_id",
          String(
            deptId
          )
        );

        localStorage.setItem(
          "exam_unit_code",
          String(
            context.unitCode
          )
        );

        localStorage.setItem(
          "exam_unit_name",
          String(
            context.unitName ||
              ""
          )
        );

        CenteredMessage.success(
          "Login Successful!"
        );

        window.location.href =
          "/exam";
      } catch (
        error
      ) {
        console.error(
          "Assessment login error:",
          error
        );

        CenteredMessage.error(
          "Network error. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

  const handleTrainingMaterial =
    () => {
      window.location.href =
        TRAINING_MATERIAL_PATH;
    };

  const handleSubmit =
    async () => {
      if (
        activeTab ===
        LOGIN_TAB.ASSESSMENT
      ) {
        await handleAssessmentLogin();

        return;
      }

      await handleSmartLogin();
    };

  const handleTabChange =
    (key) => {
      setActiveTab(
        key
      );

      setLoading(
        false
      );

      form.resetFields();

      setUsername(
        ""
      );

      setPassword(
        ""
      );

      if (
        typeof window !==
        "undefined"
      ) {
        const url =
          new URL(
            window.location.href
          );

        if (
          key ===
          LOGIN_TAB.ASSESSMENT
        ) {
          url.searchParams.set(
            "tab",
            "assessment"
          );
        } else {
          url.searchParams.delete(
            "tab"
          );
        }

        window.history.replaceState(
          {},
          "",
          `${url.pathname}${url.search}`
        );
      }
    };

  const isAssessment =
    activeTab ===
    LOGIN_TAB.ASSESSMENT;

  return (
    <div className="shahi-login-page">
      <div className="shahi-login-wrap">
        <Card
          bordered={false}
          className="shahi-login-card"
        >
          <div className="shahi-login-brand">
            <div className="shahi-login-brand-title">
              SM-ART
            </div>

            <div className="shahi-login-brand-subtitle">
              Smart Manufacturing
            </div>
          </div>

          <div className="shahi-login-heading">
            <Title
              level={3}
            >
              Welcome To Shahi
            </Title>

            <Text>
              Please enter your
              credentials to continue
            </Text>
          </div>

          <div
            key={activeTab}
            className="shahi-login-form-transition"
          >
            <Form
            form={form}
            layout="vertical"
            onFinish={
              handleSubmit
            }
            requiredMark={
              false
            }
            className="shahi-login-form"
          >
            <Form.Item
              name="username"
              label="Username"
              rules={[
                {
                  required:
                    true,

                  message:
                    "Username is required",
                },
              ]}
            >
              <Input
                prefix={
                  <UserOutlined />
                }
                placeholder="username@unit"
                value={
                  username
                }
                onChange={(
                  event
                ) =>
                  setUsername(
                    event.target.value
                  )
                }
                autoComplete="username"
              />
            </Form.Item>

            <div className="shahi-login-helper">
              <GlobalOutlined />

              <span>
                Format: username@unit
              </span>
            </div>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                {
                  required:
                    true,

                  message:
                    "Password is required",
                },
              ]}
            >
              <Input.Password
                prefix={
                  <LockOutlined />
                }
                placeholder="••••••••"
                value={
                  password
                }
                onChange={(
                  event
                ) =>
                  setPassword(
                    event.target.value
                  )
                }
                autoComplete="current-password"
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              block
              loading={
                loading
              }
              disabled={
                unitsLoading
              }
              icon={
                isAssessment
                  ? <FileDoneOutlined />
                  : <LoginOutlined />
              }
              className="shahi-login-submit"
            >
              {isAssessment
                ? "Continue to Assessment"
                : "Sign In"}
            </Button>


            <div
              className="shahi-login-mode-selector"
              role="tablist"
              aria-label="Login type"
            >
              <button
                type="button"
                role="tab"
                aria-selected={
                  activeTab ===
                  LOGIN_TAB.SMART
                }
                className={
                  activeTab ===
                  LOGIN_TAB.SMART
                    ? "shahi-login-mode-option is-active"
                    : "shahi-login-mode-option"
                }
                onClick={() =>
                  handleTabChange(
                    LOGIN_TAB.SMART
                  )
                }
              >
                <span className="shahi-login-mode-icon">
                  <LoginOutlined />
                </span>

                <span className="shahi-login-mode-text">
                  <strong>
                    SM-ART Login
                  </strong>

                  <small>
                    Operations Portal
                  </small>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={
                  activeTab ===
                  LOGIN_TAB.ASSESSMENT
                }
                className={
                  activeTab ===
                  LOGIN_TAB.ASSESSMENT
                    ? "shahi-login-mode-option is-active"
                    : "shahi-login-mode-option"
                }
                onClick={() =>
                  handleTabChange(
                    LOGIN_TAB.ASSESSMENT
                  )
                }
              >
                <span className="shahi-login-mode-icon">
                  <FileDoneOutlined />
                </span>

                <span className="shahi-login-mode-text">
                  <strong>
                    Assessment Login
                  </strong>

                  <small>
                    Take Assessment
                  </small>
                </span>
              </button>
            </div>

            {isAssessment && (
              <div className="shahi-training-material-action">
                <span className="shahi-training-material-copy">
                  Review training materials before starting your assessment.
                </span>

                <Button
                  type="text"
                  icon={<BookOutlined />}
                  onClick={handleTrainingMaterial}
                  className="shahi-training-material-button"
                >
                  View Training Materials
                </Button>
              </div>
            )}
          </Form>
          </div>
        </Card>
      </div>

      <style>{`
        @keyframes shahiLoginEnter {
          from {
            opacity: 0;
            transform: translateY(14px) scale(.995);
          }

          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .shahi-login-page {
          width: 100%;
          min-height: 100vh;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 24px;

          background:
            linear-gradient(
              135deg,
              #667eea 0%,
              #764ba2 100%
            );
        }

        .shahi-login-wrap {
          width: 100%;
          max-width: 410px;

          animation:
            shahiLoginEnter
            .32s ease-out;
        }

        .shahi-login-card.ant-card {
          overflow: hidden;

          border:
            1px solid
            rgba(255, 255, 255, .26);

          border-radius: 20px;

          background: #ffffff;

          box-shadow:
            0 24px 58px
            rgba(36, 24, 78, .20);
        }

        .shahi-login-card
          .ant-card-body {
          padding:
            34px 30px 32px;
        }

        /* ---------- Brand ---------- */

        .shahi-login-brand {
          margin-bottom: 18px;

          text-align: center;
        }

        .shahi-login-brand-title {
          font-size: 35px;
          line-height: 1;

          font-weight: 800;
          letter-spacing: 1.6px;

          background:
            linear-gradient(
              135deg,
              #667eea 0%,
              #764ba2 100%
            );

          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .shahi-login-brand-subtitle {
          margin-top: 6px;

          color: #98a2b3;

          font-size: 8.8px;
          font-weight: 600;

          letter-spacing: 1.35px;
          text-transform: uppercase;
        }

        /* ---------- Heading ---------- */

        .shahi-login-heading {
          margin-bottom: 20px;

          text-align: center;
        }

        .shahi-login-heading
          h3.ant-typography {
          margin: 0 0 5px !important;

          color: #1f2937;

          font-size: 21px;
          line-height: 1.35;

          font-weight: 700;
          letter-spacing: -.2px;
        }

        .shahi-login-heading
          .ant-typography {
          color: #748096;

          font-size: 10.5px;
          font-weight: 500;
        }

        /* ---------- Login mode selector ---------- */

        .shahi-login-mode-selector {
          width: 286px;

          display: grid;

          grid-template-columns:
            repeat(2, minmax(0, 1fr));

          gap: 8px;

          margin:
            16px auto 0;
        }

        .shahi-login-mode-option {
          min-height: 54px;

          display: flex;
          align-items: center;

          gap: 10px;

          padding:
            8px 9px;

          border:
            1px solid #e3e6ed;

          border-radius: 11px;

          background: #ffffff;

          color: #6e7787;

          text-align: left;

          cursor: pointer;

          transition:
            border-color .16s ease,
            background-color .16s ease,
            box-shadow .16s ease,
            transform .16s ease;
        }

        .shahi-login-mode-option:hover {
          border-color: #c8c2e3;

          background: #fbfaff;

          transform: translateY(-1px);
        }

        .shahi-login-mode-option.is-active {
          border-color: #9f93dc;

          background:
            linear-gradient(
              180deg,
              #fbfaff 0%,
              #f6f3ff 100%
            );

          box-shadow:
            0 5px 14px
            rgba(98, 85, 184, .10);
        }

        .shahi-login-mode-icon {
          width: 28px;
          height: 28px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 auto;

          border-radius: 8px;

          background: #f3f4f7;

          color: #8b95a5;

          font-size: 13px;

          transition:
            background-color .16s ease,
            color .16s ease;
        }

        .shahi-login-mode-option.is-active
          .shahi-login-mode-icon {
          background: #ede9ff;

          color: #6556bf;
        }

        .shahi-login-mode-text {
          min-width: 0;

          display: flex;
          flex-direction: column;
        }

        .shahi-login-mode-text
          strong {
          color: #334155;

          font-size: 10.5px;
          font-weight: 700;

          line-height: 1.25;
        }

        .shahi-login-mode-text
          small {
          margin-top: 3px;

          color: #8b96a7;

          font-size: 8px;
          font-weight: 500;

          line-height: 1.2;
        }

        .shahi-login-mode-option.is-active
          .shahi-login-mode-text
          strong {
          color: #5f51b6;
        }

        /* ---------- Form ---------- */

        .shahi-login-form
          .ant-form-item {
          margin-bottom: 15px;
        }

        .shahi-login-form
          .ant-form-item-label {
          padding-bottom: 5px;
        }

        .shahi-login-form
          .ant-form-item-label
          > label {
          height: auto;

          color: #344054;

          font-size: 10px;
          font-weight: 650;
        }

        .shahi-login-form
          .ant-input-affix-wrapper {
          width: 100%;
          height: 40px;

          padding-inline: 11px;

          border:
            1px solid #dfe4ec;

          border-radius: 9px;

          background: #ffffff;

          box-shadow: none;

          transition:
            border-color .16s ease,
            box-shadow .16s ease;
        }

        .shahi-login-form
          .ant-input-affix-wrapper:hover {
          border-color: #b9b2d7;
        }

        .shahi-login-form
          .ant-input-affix-wrapper-focused {
          border-color: #7365c6 !important;

          box-shadow:
            0 0 0 3px
            rgba(105, 86, 190, .09) !important;
        }

        .shahi-login-form
          .ant-input {
          color: #344054;

          font-size: 10.5px;
          font-weight: 500;
        }

        .shahi-login-form
          .ant-input-prefix {
          margin-right: 7px;

          color: #929bad;

          font-size: 11px;
        }

        .shahi-login-helper {
          display: flex;
          align-items: center;

          gap: 5px;

          margin:
            -8px 0 12px 2px;

          color: #8b96a7;

          font-size: 8.3px;
          font-weight: 500;
        }

        /* ---------- Submit ---------- */

        .shahi-login-submit.ant-btn {
          width: 174px !important;
          min-width: 174px !important;
          height: 38px !important;

          display: flex !important;
          align-items: center !important;
          justify-content: center !important;

          margin:
            7px auto 0 !important;

          padding:
            0 16px !important;

          border: none !important;
          border-radius: 9px !important;

          background:
            linear-gradient(
              135deg,
              #667eea 0%,
              #764ba2 100%
            ) !important;

          color: #ffffff !important;

          font-size: 10.5px !important;
          font-weight: 650 !important;

          box-shadow:
            0 6px 14px
            rgba(91, 70, 170, .20) !important;
        }

        .shahi-login-submit.ant-btn:hover {
          transform: translateY(-1px);

          box-shadow:
            0 8px 18px
            rgba(91, 70, 170, .25) !important;
        }

        /* ---------- Mobile ---------- */

        @media (max-width: 520px) {
          .shahi-login-page {
            align-items: flex-start;

            padding: 16px 10px;

            overflow-y: auto;
          }

          .shahi-login-wrap {
            max-width: 380px;

            margin: auto 0;
          }

          .shahi-login-card
            .ant-card-body {
            padding:
              28px 20px 26px;
          }

          .shahi-login-mode-selector {
            gap: 8px;
          }

          .shahi-login-mode-option {
            min-height: 58px;

            padding:
              9px;
          }

          .shahi-login-mode-text
            strong {
            font-size: 9.7px;
          }

          .shahi-login-mode-text
            small {
            font-size: 7.6px;
          }
        }

        @media (max-width: 360px) {
          .shahi-login-mode-selector {
            grid-template-columns:
              1fr;
          }

          .shahi-login-card
            .ant-card-body {
            padding:
              24px 15px 23px;
          }

          .shahi-login-submit.ant-btn {
            width: 160px !important;
            min-width: 160px !important;
          }
        }


        /* =========================================
           FINAL FORM PROPORTIONS
           Narrower fields + better field height
           ========================================= */

        .shahi-login-form {
          width: 100%;
          max-width: 318px;

          margin:
            0 auto;
        }

        .shahi-login-form
          .ant-form-item {
          width: 100%;

          margin-bottom:
            16px !important;
        }

        .shahi-login-form
          .ant-form-item-label {
          padding-bottom:
            5px !important;
        }

        .shahi-login-form
          .ant-form-item-label
          > label {
          font-size:
            10.5px !important;

          font-weight:
            650 !important;
        }

        .shahi-login-form
          .ant-input-affix-wrapper {
          width:
            100% !important;

          height:
            44px !important;

          padding-inline:
            12px !important;

          border-radius:
            9px !important;
        }

        .shahi-login-form
          .ant-input {
          font-size:
            11px !important;

          line-height:
            1.4 !important;
        }

        .shahi-login-form
          .ant-input-prefix {
          margin-right:
            8px !important;

          font-size:
            12px !important;
        }

        .shahi-login-helper {
          width:
            100%;

          margin:
            -9px 0 13px 2px !important;

          font-size:
            8.4px !important;
        }

        .shahi-login-submit.ant-btn {
          width:
            168px !important;

          min-width:
            168px !important;

          height:
            40px !important;

          margin:
            8px auto 0 !important;
        }

        @media (max-width: 520px) {
          .shahi-login-form {
            max-width:
              100%;
          }

          .shahi-login-form
            .ant-input-affix-wrapper {
            height:
              43px !important;
          }
        }



        /* =========================================
           FINAL FORM PROPORTIONS - STRONG PX
           Clearly narrower + taller inputs
           ========================================= */

        .shahi-login-form {
          width: 286px !important;
          max-width: 286px !important;

          margin:
            0 auto !important;
        }

        .shahi-login-form
          .ant-form-item {
          width: 286px !important;

          margin-bottom:
            18px !important;
        }

        .shahi-login-form
          .ant-form-item-label {
          padding-bottom:
            6px !important;
        }

        .shahi-login-form
          .ant-form-item-label
          > label {
          font-size:
            11px !important;

          font-weight:
            650 !important;
        }

        .shahi-login-form
          .ant-input-affix-wrapper {
          width:
            286px !important;

          min-width:
            286px !important;

          max-width:
            286px !important;

          height:
            48px !important;

          min-height:
            48px !important;

          padding:
            0 13px !important;

          border-radius:
            10px !important;
        }

        .shahi-login-form
          .ant-input {
          height:
            44px !important;

          font-size:
            11.5px !important;

          line-height:
            44px !important;
        }

        .shahi-login-form
          .ant-input-prefix {
          margin-right:
            9px !important;

          font-size:
            13px !important;
        }

        .shahi-login-form
          .ant-input-password-icon {
          font-size:
            13px !important;
        }

        .shahi-login-helper {
          width:
            286px !important;

          margin:
            -10px 0 15px 2px !important;

          font-size:
            8.5px !important;
        }

        .shahi-login-submit.ant-btn {
          width:
            156px !important;

          min-width:
            156px !important;

          height:
            40px !important;

          margin:
            10px auto 0 !important;
        }

        @media (max-width: 520px) {
          .shahi-login-form,
          .shahi-login-form
            .ant-form-item,
          .shahi-login-form
            .ant-input-affix-wrapper,
          .shahi-login-helper {
            width:
              100% !important;

            min-width:
              0 !important;

            max-width:
              100% !important;
          }

          .shahi-login-form
            .ant-input-affix-wrapper {
            height:
              46px !important;

            min-height:
              46px !important;
          }

          .shahi-login-form
            .ant-input {
            height:
              42px !important;

            line-height:
              42px !important;
          }
        }



        /* =========================================
           LOGIN TAB CHANGE ANIMATION
           ========================================= */

        @keyframes shahiLoginFormSwitch {
          from {
            opacity: 0;
            transform:
              translateY(8px);
          }

          to {
            opacity: 1;
            transform:
              translateY(0);
          }
        }

        @keyframes shahiLoginModePulse {
          0% {
            transform:
              scale(.985);
          }

          100% {
            transform:
              scale(1);
          }
        }

        .shahi-login-form-transition {
          width: 100%;

          animation:
            shahiLoginFormSwitch
            220ms
            cubic-bezier(.22, .8, .22, 1)
            both;
        }

        .shahi-login-mode-option {
          position: relative;

          transition:
            border-color 180ms ease,
            background-color 180ms ease,
            color 180ms ease,
            box-shadow 180ms ease,
            transform 180ms
            cubic-bezier(.22, .8, .22, 1)
            !important;
        }

        .shahi-login-mode-option.is-active {
          animation:
            shahiLoginModePulse
            190ms
            ease-out;
        }

        .shahi-login-mode-option
          .shahi-login-mode-icon {
          transition:
            background-color 180ms ease,
            color 180ms ease,
            transform 180ms ease
            !important;
        }

        .shahi-login-mode-option.is-active
          .shahi-login-mode-icon {
          transform:
            scale(1.06);
        }

        .shahi-login-mode-option
          .shahi-login-mode-text
          strong,
        .shahi-login-mode-option
          .shahi-login-mode-text
          small {
          transition:
            color 180ms ease,
            opacity 180ms ease
            !important;
        }

        .shahi-login-submit.ant-btn {
          transition:
            transform 180ms ease,
            box-shadow 180ms ease,
            opacity 180ms ease
            !important;
        }

        @media (
          prefers-reduced-motion: reduce
        ) {
          .shahi-login-form-transition,
          .shahi-login-mode-option,
          .shahi-login-mode-icon {
            animation:
              none !important;

            transition:
              none !important;
          }
        }



        /* =========================================
           ASSESSMENT TRAINING MATERIAL ACTION
           ========================================= */

        .shahi-training-material-action {
          width: 286px;

          display: flex;
          flex-direction: column;
          align-items: center;

          gap: 4px;

          margin:
            13px auto 0;

          padding-top:
            12px;

          border-top:
            1px solid #edf0f5;
        }

        .shahi-training-material-copy {
          color: #8a95a6;

          font-size: 8.5px;
          font-weight: 500;

          text-align: center;
          line-height: 1.45;
        }

        .shahi-training-material-button.ant-btn {
          height: 30px;

          padding:
            0 8px;

          border-radius: 7px;

          color: #6255b8;

          font-size: 9.5px;
          font-weight: 650;

          box-shadow: none;
        }

        .shahi-training-material-button.ant-btn:hover,
        .shahi-training-material-button.ant-btn:focus {
          background: #f5f2ff !important;

          color: #5145a6 !important;
        }

        .shahi-training-material-button
          .anticon {
          font-size: 11px;
        }

        @media (max-width: 520px) {
          .shahi-training-material-action {
            width: 100%;
          }
        }


        @media (max-width: 520px) {
          .shahi-login-mode-selector {
            width: 100%;
            margin-top: 14px;
          }
        }



        /* =========================================
           LOGIN MODE TEXT - NO OVERFLOW / NO WRAP
           ========================================= */

        .shahi-login-mode-selector {
          width: 300px !important;

          grid-template-columns:
            repeat(2, minmax(0, 1fr)) !important;

          gap: 8px !important;
        }

        .shahi-login-mode-option {
          min-width: 0 !important;

          padding:
            8px 8px !important;

          gap:
            7px !important;
        }

        .shahi-login-mode-icon {
          width:
            26px !important;

          height:
            26px !important;

          flex:
            0 0 26px !important;

          font-size:
            11px !important;
        }

        .shahi-login-mode-text {
          min-width:
            0 !important;

          width:
            100%;

          overflow:
            hidden;
        }

        .shahi-login-mode-text
          strong {
          display:
            block;

          max-width:
            100%;

          overflow:
            hidden;

          color:
            #334155;

          font-size:
            9.6px !important;

          font-weight:
            700 !important;

          line-height:
            1.2 !important;

          text-overflow:
            ellipsis;

          white-space:
            nowrap !important;
        }

        .shahi-login-mode-text
          small {
          display:
            block;

          max-width:
            100%;

          margin-top:
            3px;

          overflow:
            hidden;

          color:
            #7f8b9b !important;

          font-size:
            7.8px !important;

          font-weight:
            550 !important;

          line-height:
            1.2 !important;

          text-overflow:
            ellipsis;

          white-space:
            nowrap !important;
        }

        @media (max-width: 520px) {
          .shahi-login-mode-selector {
            width:
              100% !important;
          }

          .shahi-login-mode-text
            strong {
            font-size:
              9.3px !important;
          }

          .shahi-login-mode-text
            small {
            font-size:
              7.5px !important;
          }
        }

        @media (max-width: 360px) {
          .shahi-login-mode-selector {
            grid-template-columns:
              1fr !important;
          }

          .shahi-login-mode-text
            strong {
            font-size:
              10px !important;
          }

          .shahi-login-mode-text
            small {
            font-size:
              8px !important;
          }
        }

      `}</style>
    </div>
  );
}

export default UserAuth;
